import { useAuth } from '@utils/auth/AuthProvider';
import { safeReloadForReauth } from '@utils/errorUtils';
import { useEffect } from 'react';
import { AuthGuardProps } from 'types/Auth.types';

import AuthLoading from './AuthLoading';

/**
 * AuthGuard Component
 *
 * This component protects routes and content that require authentication.
 * It acts as a wrapper around protected content and handles:
 *
 * - Displaying a loading state while authentication is being checked
 * - Redirecting unauthenticated users to the sign-in flow
 * - Rendering protected content only when the user is authenticated
 * - Preserving the current URL for post-authentication redirect
 *
 * The AuthContext is used to access authentication state and methods.
 */
const AuthGuard = ({ children }: AuthGuardProps) => {
  const { isAuthenticated, loading, signIn } = useAuth();

  // Redirect unauthenticated users to sign-in
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Store the current URL to redirect back after authentication
      localStorage.setItem('redirectAfterAuth', window.location.pathname);

      // Trigger sign in
      signIn().catch((signInError) => {
        console.error('Failed to sign in:', signInError);
        // If config cookie is missing, reload to re-fetch from CloudFront
        if (signInError instanceof Error && signInError.message.includes('Cognito settings cookie not found')) {
          safeReloadForReauth();
        }
      });
    }
  }, [loading, isAuthenticated, signIn]);

  // Show loading screen while checking authentication or during redirect
  if (loading || !isAuthenticated) {
    return <AuthLoading />;
  }

  // If authenticated, render the protected content
  return children;
};

export default AuthGuard;
